import { apiRequest } from "@data/redux/actions/api"
import {
    resolveParentFamilyAccounts,
    selectSessionStoredTokens,
} from "@data/utility/familyUtils"
import { isBlockOn, TENANT_BLOCKS } from "@data/utility/tenantBlocks"
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs"
import React, { useCallback, useEffect, useState } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import { switchAccount } from "../../../data/redux/actions/authTokens"
import { resetIntercomIdentification } from "../../../data/redux/actions/intercom"
import { setIsParentMode } from "../../../data/redux/actions/parentMode"
import { authService } from "../../../data/services/authService"
import { subscriptionService } from "../../../data/services/subscriptionService"
import * as Device from "expo-device"
import { useLogout } from "../../account/LogoutButton"
import { useApiStatus } from "../../hooks/useApiStatus"
import IntercomChat, {
    intercomName,
    useOpenIntercomChat,
} from "@common/components/chat/IntercomChat"
import NotificationSubscriptionHandler from "@common/notifications/NotificationSubscriptionHandler"
import ForceUpdateGuard from "../modal/ForceUpdateGuard"
import { IS_WEB } from "../../utils/mobileUtils"
import { LoadingScreen } from "@common/screens/LoadingScreen"
import { ParentLayoutContext } from "./ParentLayoutContext"
import { parentTabScreens } from "./parentTabScreens"
import { ParentStatusBar } from "../../../ports/primary/ui/screens/parent/ParentStatusBar"
import { ParentTabBar } from "../../../ports/primary/ui/screens/parent/ParentTabBar"

const Tab = createBottomTabNavigator()

const ParentMainLayout = (props) => {
    const tabScreens = parentTabScreens(props.user) || []

    // All state management - executed once
    const [isLoggingOut, setIsLoggingOut] = useState(false)

    // Single support entry point for the whole parent app — Intercom chat.
    // Contextual surfaces (e.g. "Changer d'offre" in the subscription portal)
    // call this to open the same messenger the HelpFAB does; it refreshes a
    // stale session first so the chat never opens on an expired token.
    const openSupportChat = useOpenIntercomChat()

    // Initialize logout functionality
    const [onLogout] = useLogout(props)
    // Le seul geste de déconnexion de l'espace : le bouton qui l'a lancé tourne jusqu'à l'issue.
    const startLogout = useCallback(() => {
        setIsLoggingOut(true)
        onLogout()
    }, [onLogout])

    // Non-blocking refresh of family tokens on entering parent mode
    useEffect(() => {
        props.apiRequest(
            authService.getFamilyTokens,
            {},
            { device_name: IS_WEB ? "WEB" : (Device.deviceName ?? "mobile") }
        )
        // When parent mode is entered via FamilyAccountSelection, switchAccount
        // wipes currentUser to minimal token fields before BottomTabsNavigator
        // unmounts — fetch the full user so the header has referrer_dashboard_url.
        props.apiRequest(authService.getUser)
    }, [])

    const selectedFamilyId = props.familyState?.parentModeFamilyId

    // Reset Intercom identification when the active parent-mode user changes
    // (family switch or sibling switch within a family) so the next render
    // refetches a fresh JWT for the new user_id.
    useEffect(() => {
        return () => {
            props.resetIntercomIdentification()
        }
    }, [selectedFamilyId, props.user?.id])

    const user = props.user

    const familyAccounts = resolveParentFamilyAccounts(
        props.storedTokens,
        props.familyState,
        user
    )

    // Initialize selectedUser with the account that matches props.user.id
    const [selectedUser, setSelectedUser] = useState(() => {
        if (user?.id && familyAccounts.length > 0) {
            return (
                familyAccounts.find((account) => account.userId === user.id) ||
                familyAccounts[0]
            )
        }
        return familyAccounts[0] || null
    })

    // Account switching with loading state
    const [switchingAccountId, setSwitchingAccountId] = useState(null)

    useApiStatus(
        subscriptionService.getSubscribePricing,
        null,
        false,
        () => setSwitchingAccountId(null),
        () => setSwitchingAccountId(null)
    )

    const handleUserSelection = (account) => {
        if (account.userId === selectedUser?.userId) return
        if (!account.token) return
        setSwitchingAccountId(account.userId)
        setSelectedUser(account)
        props.switchAccount(account)
        // SWITCH_ACCOUNT only hydrates minimal token fields — fetch full user data.
        props.apiRequest(authService.getUser)
    }

    // Initialize selectedUser when familyAccounts are available or user changes
    useEffect(() => {
        if (familyAccounts.length > 0) {
            if (user?.id) {
                // Find the account that matches the current user
                const matchingAccount = familyAccounts.find(
                    (account) => account.userId === user.id
                )
                if (
                    matchingAccount &&
                    (!selectedUser || selectedUser.userId !== user.id)
                ) {
                    setSelectedUser(matchingAccount)
                }
            } else if (!selectedUser) {
                // Fallback to first account if no user or selectedUser
                setSelectedUser(familyAccounts[0])
            }
        }
    }, [familyAccounts.length, user?.id])

    // Handle logout success and navigate to family account selection
    useApiStatus(authService.logout, null, true, () => {
        // On logout success, navigate to family account selection
        setIsLoggingOut(false)
    }, () => {
        // En échec aussi : sinon le bouton tourne jusqu'au rechargement (web), et la feuille
        // « Changer de profil » garde ses lignes inertes. Un échec de `getFamilyTokens`, lui,
        // n'arrête rien : `useLogout` enchaîne quand même sur `logout`.
        setIsLoggingOut(false)
    })

    // Sous impersonation, la déconnexion sort de l'impersonation (`useLogout`) : un échec de cette
    // sortie rend la main aussi, sans quoi le bouton tournerait jusqu'au rechargement.
    useApiStatus(authService.exitImpersonation, null, false, () => {}, () => {
        setIsLoggingOut(false)
    })

    // Exit parent mode: switch immediately, student screens will refresh data on mount
    const handleExitParentMode = () => {
        props.setIsParentMode(false)
    }

    // Filet de sécurité : l'espace parent n'existe pas dans l'espace (tenant) de
    // l'élève. Les entrées sont cachées, mais certaines basculent avant que
    // l'utilisateur complet soit chargé (sélection de compte, impersonation,
    // lien profond) : dès que `getUser` l'a posé, on revient côté élève.
    const parentsOn = isBlockOn(user, TENANT_BLOCKS.PARENTS)
    useEffect(() => {
        if (!parentsOn) handleExitParentMode()
    }, [parentsOn])

    // Exit parent mode and switch to a specific child's student view
    const handleExitToChild = (account) => {
        if (account.token) {
            props.switchAccount(account)
        }
        props.setIsParentMode(false)
    }

    // Context value to share with all tab screens
    const contextValue = {
        familyAccounts,
        selectedUser,
        handleUserSelection,
        handleExitToChild,
        switchingAccountId,
        isLoggingOut,
        startLogout,
        user,
        openSupportChat,
    }

    if (!tabScreens || tabScreens.length === 0) {
        return null
    }

    return (
        <ParentLayoutContext.Provider value={contextValue}>
            <View style={{ flex: 1 }}>
                <ForceUpdateGuard />
                {/* No user yet (e.g. an account switch into parent mode wipes
                    currentUser before the full reload lands) — show a loader,
                    not a blank screen. Resolves to the tabs once user is back. */}
                {props.user ? (
                    <Tab.Navigator
                        tabBar={(tabBarProps) => (
                            <ParentTabBar {...tabBarProps} />
                        )}
                        screenOptions={{
                            tabBarShowLabel: false,
                            unmountOnBlur: false,
                            headerShown: false,
                        }}
                        sceneAnimationEnabled={false}
                    >
                        {tabScreens.map((screen) => {
                            return (
                                <Tab.Screen
                                    key={screen.name}
                                    name={screen.name}
                                    component={screen.component}
                                    options={screen.options}
                                />
                            )
                        })}
                    </Tab.Navigator>
                ) : (
                    <LoadingScreen />
                )}

                {/* Parent layout is a separate navigator from BottomTabsNavigator,
                    so mount the handler here too — without it, a support-reply
                    push tap isn't handled in parent mode. */}
                {!IS_WEB && <NotificationSubscriptionHandler />}

                {/* Intercom Chat — initializes SDK for the current parent. */}
                <IntercomChat
                    userId={user?.id}
                    email={user?.email}
                    name={intercomName(user)}
                />

                {/* Cette barre est montée APRÈS celle du header, donc c'est elle qui gagne :
                    codée en dur, elle laissait l'heure en noir sur le header nuit. Elle suit le
                    thème. */}
                <ParentStatusBar />
            </View>
        </ParentLayoutContext.Provider>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        storedTokens: selectSessionStoredTokens(state),
        familyState: state.data.family || { families: {}, userFamilyMap: {} },
    }
}

const mapDispatchToProps = {
    apiRequest,
    switchAccount,
    setIsParentMode,
    resetIntercomIdentification,
}

export default connect(mapStateToProps, mapDispatchToProps)(ParentMainLayout)
